import { createApp } from 'vue'
import { abilitiesPlugin } from '@casl/vue'

import App from '@/App.vue'
import {
  router,
  pinia,
  UNAUTHENTICATED_EVENT,
  i18n,
  getLocale,
  loadLocaleMessages,
  Toast,
  toastOptions,
  interceptors,
  initSentry,
  http,
  ability,
  permissions,
} from './plugins'
import { laravelEcho } from './providers'
import {
  handleSessionExpired,
  performLogout,
  registerCrossTabLogout,
} from '@/composables/auth/useLogout'
import { hydrateSession } from '@/composables/auth/useSession'
import { DEV_TOOLS } from '@/utils/constants'

export const app = createApp(App)

app.use(pinia)
app.use(permissions)
app.use(router)
app.use(i18n)
app.use(Toast, toastOptions)
app.use(interceptors)

// Deliberately WITHOUT `useGlobalProperties: true` to avoid colliding with `$can(path)`.
app.use(abilitiesPlugin, ability)

// The transport dispatches this instead of importing the router, which is what
// keeps the HTTP layer free of a routing dependency.
window.addEventListener(UNAUTHENTICATED_EVENT, () => {
  void handleSessionExpired()
})

// Signing out in one tab signs out the rest.
registerCrossTabLogout()

initSentry(app, router)

app.provide('$echo', laravelEcho)

// e2e specs drive the app's own axios instance and sign-out rather than hand-rolled
// stand-ins, so a regression in either fails the suite. Dev and e2e builds only.
if (DEV_TOOLS) {
  Object.assign(window, { __http: http, __performLogout: performLogout })
}

/**
 * Resolves once the app is ready to mount. The active locale is fetched before
 * mounting so components never render a frame of untranslated keys, and the
 * session is hydrated so the first navigation guard already knows who is signed
 * in. Neither step rejects, so a cold backend still yields a usable app.
 */
export async function bootstrap() {
  await loadLocaleMessages(getLocale())
  await hydrateSession()
  return app
}

export { router }
